/**
 * GA4 Event Utilities for consistent and reliable vehicle analytics
 * 
 * These utilities ensure all GA4 ecommerce events include required parameters
 * and provide a consistent interface for vehicle-related analytics.
 */

export interface VehicleData {
    vin: string;
    /** Stock number; the Merchant Center vehicle feed's product `id`. */
    stockId?: string;
    name: string;
    price?: number;
    make?: string;
    model?: string;
    year?: number;
}

/** Lead types that can produce a Google Ads / GA4 lead conversion. */
export type LeadType =
    | "contact"
    | "test_drive"
    | "check_availability"
    | "walkaround_video"
    | "financing"
    | "expert_consultation";

/**
 * Fixed lead values (USD) sent as `value` on generate_lead and on the Google
 * Ads conversion. Lead events used to carry the vehicle price as `value`, which
 * made a lead on an $86k car look 1,700x more valuable than a financing lead.
 * Keep these consistent so Ads value-based bidding sees comparable numbers.
 */
export const LEAD_VALUES: Record<LeadType, number> = {
    contact: 50,
    test_drive: 50,
    check_availability: 50,
    walkaround_video: 25,
    financing: 50,
    expert_consultation: 75
};

export interface DealerData {
    name?: string;
    state?: string;
}

export interface AnalyticsContext {
    isMobile?: boolean;
    formType?: "contact" | "test_drive" | "check_availability";
    buttonLocation?: "hero" | "sidebar" | "mobile_modal" | "car_card" | "drawer";
    documentType?: "window_sticker" | "vehicle_history";
}

/**
 * Sanitizes vehicle data to ensure all values are properly formatted for GA4
 */
function sanitizeVehicleData(vehicleData: VehicleData): VehicleData {
    const sanitized: VehicleData = {
        vin: vehicleData.vin || "",
        name: vehicleData.name ? vehicleData.name.trim() : "",
        make: vehicleData.make ? String(vehicleData.make).trim() : "",
        model: vehicleData.model ? String(vehicleData.model).trim() : ""
    };
    
    // Only include price if it's a valid number
    if (typeof vehicleData.price === 'number' && vehicleData.price > 0) {
        sanitized.price = vehicleData.price;
    }
    
    // Only include year if it's a valid year
    if (typeof vehicleData.year === 'number' && vehicleData.year > 1900 && vehicleData.year <= new Date().getFullYear() + 1) {
        sanitized.year = vehicleData.year;
    }
    
    return sanitized;
}

/**
 * Sanitizes dealer data to ensure all values are properly formatted for GA4
 */
function sanitizeDealerData(dealerData?: DealerData): DealerData | undefined {
    if (!dealerData) return undefined;
    
    const sanitized: DealerData = {};
    if (dealerData.name && String(dealerData.name).trim()) {
        sanitized.name = String(dealerData.name).trim();
    }
    if (dealerData.state && String(dealerData.state).trim()) {
        sanitized.state = String(dealerData.state).trim();
    }
    
    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

/**
 * Creates base event data with all required GA4 ecommerce parameters
 */
export function createBaseEventData(vehicleData: VehicleData, dealerData?: DealerData) {
    const sanitizedVehicle = sanitizeVehicleData(vehicleData);
    const sanitizedDealer = sanitizeDealerData(dealerData);
    
    const baseData: any = {
        currency: "USD",
        item_id: sanitizedVehicle.vin,
        item_name: sanitizedVehicle.name,
        item_category: "vehicle",
        item_category2: sanitizedVehicle.make,
        item_category3: sanitizedVehicle.model
    };
    
    // Only include optional fields if they have valid values
    if (typeof sanitizedVehicle.price === 'number') {
        baseData.value = sanitizedVehicle.price;
    }
    
    if (typeof sanitizedVehicle.year === 'number') {
        baseData.item_variant = sanitizedVehicle.year.toString();
    }
    
    if (sanitizedDealer?.name) {
        baseData.dealer_name = sanitizedDealer.name;
    }
    
    if (sanitizedDealer?.state) {
        baseData.dealer_location = sanitizedDealer.state;
    }
    
    return baseData;
}

/**
 * Gets device type based on context or browser detection
 */
function getDeviceType(context?: AnalyticsContext): string {
    if (context?.isMobile !== undefined) {
        return context.isMobile ? "mobile" : "desktop";
    }
    
    // Fallback to window detection if available
    if (typeof window !== 'undefined') {
        return window.innerWidth < 768 ? "mobile" : "desktop";
    }
    
    return "unknown";
}

/**
 * Creates wishlist event data (add_to_wishlist / remove_from_wishlist)
 */
export function createWishlistEventData(
    vehicleData: VehicleData,
    context?: AnalyticsContext,
    dealerData?: DealerData
) {
    return {
        ...createBaseEventData(vehicleData, dealerData),
        custom_event_type: "vehicle_save",
        interaction_location: context?.buttonLocation || "unknown",
        device_type: getDeviceType(context)
    };
}

/**
 * Creates checkout event data (begin_checkout)
 */
export function createCheckoutEventData(
    vehicleData: VehicleData,
    context?: AnalyticsContext,
    dealerData?: DealerData
) {
    return {
        ...createBaseEventData(vehicleData, dealerData),
        checkout_step: 1,
        checkout_option: "contact_dealer",
        custom_event_type: "vehicle_inquiry",
        interaction_location: context?.buttonLocation || "unknown",
        device_type: getDeviceType(context)
    };
}

/**
 * Creates cart event data (add_to_cart)
 */
export function createCartEventData(
    vehicleData: VehicleData,
    context?: AnalyticsContext,
    dealerData?: DealerData
) {
    return {
        ...createBaseEventData(vehicleData, dealerData),
        custom_event_type: "test_drive_request",
        interaction_location: context?.buttonLocation || "unknown",
        device_type: getDeviceType(context)
    };
}

/**
 * Creates lead generation event data (generate_lead)
 */
export function createLeadEventData(
    leadType: "contact" | "test_drive" | "check_availability" | "walkaround_video",
    vehicleData: VehicleData,
    context?: AnalyticsContext,
    dealerData?: DealerData
) {
    const base = createBaseEventData(vehicleData, dealerData);
    const vehiclePrice = typeof base.value === "number" ? base.value : undefined;
    return {
        ...base,
        // `value` is the lead's worth, not the sticker price; keep the price separately.
        value: LEAD_VALUES[leadType],
        ...(vehiclePrice !== undefined && { vehicle_price: vehiclePrice }),
        items: [toGAItem(vehicleData, dealerData)],
        lead_type: leadType,
        custom_event_type: "form_submission",
        form_type: leadType,
        interaction_location: context?.buttonLocation || "unknown",
        device_type: getDeviceType(context)
    };
}

/**
 * Creates promotion event data (view_promotion / select_promotion)
 */
export function createPromotionEventData(
    promotionType: "financing" | "trade_in",
    vehicleData: VehicleData,
    context?: AnalyticsContext,
    dealerData?: DealerData
) {
    return {
        ...createBaseEventData(vehicleData, dealerData),
        promotion_id: promotionType,
        promotion_name: promotionType === "financing" ? "Get Financing" : "Trade-In Value",
        custom_event_type: "promotion_view",
        interaction_location: context?.buttonLocation || "unknown",
        device_type: getDeviceType(context)
    };
}

/**
 * Creates view item event data (view_item)
 */
export function createViewItemEventData(
    itemType: "vehicle" | "document" | "walkaround_video",
    vehicleData: VehicleData,
    context?: AnalyticsContext,
    dealerData?: DealerData
) {
    const base = createBaseEventData(vehicleData, dealerData);
    // Google Ads vehicle remarketing matches `id` against the Merchant Center
    // feed id, which is the stock number. The GTM remarketing tag reads the
    // top-level item_id on view_item, so it carries the stock number here;
    // items[].item_id and `vin` keep the VIN for GA4.
    const feedId = itemType === "vehicle" ? vehicleData.stockId?.trim() || base.item_id : base.item_id;
    return {
        ...base,
        ...(itemType === "vehicle" && base.item_id && {
            item_id: feedId,
            vin: base.item_id,
            items: [{ ...toGAItem(vehicleData, dealerData), id: feedId, google_business_vertical: "vehicle" }]
        }),
        content_type: itemType,
        custom_event_type: itemType === "document" ? "document_view" : "vehicle_view",
        ...(context?.documentType && { document_type: context.documentType }),
        interaction_location: context?.buttonLocation || "unknown",
        device_type: getDeviceType(context)
    };
}

/**
 * Creates share event data (share)
 */
export function createShareEventData(
    shareMethod: "native" | "clipboard",
    vehicleData: VehicleData,
    context?: AnalyticsContext,
    dealerData?: DealerData
) {
    return {
        ...createBaseEventData(vehicleData, dealerData),
        method: shareMethod,
        content_type: "vehicle",
        custom_event_type: "vehicle_share",
        interaction_location: context?.buttonLocation || "unknown",
        device_type: getDeviceType(context)
    };
}

/**
 * Creates form interaction event data (form_start / form_submit)
 */
export function createFormEventData(
    formType: "contact" | "test_drive" | "check_availability" | "walkaround_video",
    vehicleData: VehicleData,
    context?: AnalyticsContext,
    dealerData?: DealerData
) {
    return {
        ...createBaseEventData(vehicleData, dealerData),
        form_type: formType,
        custom_event_type: "form_interaction",
        interaction_location: context?.buttonLocation || "unknown",
        device_type: getDeviceType(context)
    };
}

/**
 * Creates phone click event data (custom event)
 */
export function createPhoneClickEventData(
    phoneNumber: string,
    vehicleData: VehicleData,
    context?: AnalyticsContext,
    dealerData?: DealerData
) {
    return {
        ...createBaseEventData(vehicleData, dealerData),
        // Avoid sending PII (phone numbers) to GA4; record generic interaction only
        custom_event_type: "contact_phone",
        interaction_location: context?.buttonLocation || "unknown",
        device_type: getDeviceType(context)
    };
}

/**
 * Creates test drive click event data (custom event)
 */
export function createTestDriveClickEventData(
    vehicleData: VehicleData,
    context?: AnalyticsContext,
    dealerData?: DealerData
) {
    return {
        ...createBaseEventData(vehicleData, dealerData),
        custom_event_type: "test_drive_interest",
        interaction_location: context?.buttonLocation || "unknown",
        device_type: getDeviceType(context)
    };
}

/**
 * Creates financing click event data (custom event)
 */
export function createFinancingClickEventData(
    provider: string,
    vehicleData: VehicleData,
    context?: AnalyticsContext,
    dealerData?: DealerData
) {
    return {
        ...createBaseEventData(vehicleData, dealerData),
        financing_provider: provider,
        custom_event_type: "financing_interest",
        interaction_location: context?.buttonLocation || "unknown",
        device_type: getDeviceType(context)
    };
}

/**
 * Convert vehicle data into a GA4 item object for ecommerce events
 */
export function toGAItem(vehicleData: VehicleData, dealerData?: DealerData) {
    const base = createBaseEventData(vehicleData, dealerData) as any;
    const item: any = {
        item_id: base.item_id,
        item_name: base.item_name,
        item_category: base.item_category,
        item_category2: base.item_category2,
        item_category3: base.item_category3
    };
    if (base.item_variant) item.item_variant = base.item_variant;
    if (typeof base.value === 'number') item.price = base.value;
    if (base.dealer_name) item.dealer_name = base.dealer_name;
    if (base.dealer_location) item.dealer_location = base.dealer_location;
    return item;
}
